/**
 * Pure client selection logic for testing and ClientContext.
 * Given a list of clients and context (user email, stored name, hostname), returns the client to select.
 */

export interface ClientForSelection {
  clientName: string;
  name: string;
  primaryDomain: string;
  promptCount: number;
  domains: string[];
  competitors: Array<{ name: string; urls: string[] }>;
  businessSummary: string;
}

export interface SelectClientOptions {
  userEmail: string | null;
  storedClientName: string | null;
  hostname: string;
  preferredClientName?: string | null;
}

export function selectClientForUser(
  clientsData: ClientForSelection[],
  options: SelectClientOptions
): ClientForSelection | null {
  const { storedClientName, hostname, preferredClientName } = options;
  if (clientsData.length === 0) return null;

  let clientToSelect: ClientForSelection | null = null;

  if (preferredClientName) {
    clientToSelect = clientsData.find((c) => c.clientName === preferredClientName) || null;
    if (!clientToSelect) {
      const norm = (d: string) => d.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '').toLowerCase();
      const preferredNorm = norm(preferredClientName);
      clientToSelect =
        clientsData.find(
          (c) =>
            norm(c.primaryDomain || '') === preferredNorm ||
            (c.domains || []).some((d: string) => norm(d) === preferredNorm)
        ) || null;
    }
  }

  // Stored choice before hostname heuristics so shared hosting / appspot URLs don't steal selection on refresh
  if (!clientToSelect && storedClientName) {
    clientToSelect = clientsData.find((c) => c.clientName === storedClientName) || null;
  }

  if (!clientToSelect) {
    const parts = hostname.split('.');
    const baseDomain = parts.length >= 2 ? parts.slice(-2).join('.') : hostname;
    const candidateDomains = [hostname, baseDomain].filter(Boolean);
    const normalize = (d: string) => d.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '').toLowerCase();
    for (const c of clientsData) {
      const primaryNorm = normalize(c.primaryDomain || '');
      const domainNorms = (c.domains || []).map((d: string) => normalize(d));
      const allNorms = [primaryNorm, ...domainNorms].filter(Boolean);
      const clientNameNorm = (c.clientName || '').toLowerCase().replace(/_/g, '.');
      const matchesDomain = candidateDomains.some(
        (cand) =>
          allNorms.includes(cand) || allNorms.some((n) => n === cand || n.endsWith('.' + cand))
      );
      const matchesBaseDomainAsClient =
        baseDomain && (baseDomain === clientNameNorm || baseDomain.startsWith(clientNameNorm + '.'));
      if (matchesDomain || matchesBaseDomainAsClient) {
        clientToSelect = c;
        break;
      }
    }
  }
  if (!clientToSelect) {
    clientToSelect = clientsData[0];
  }
  return clientToSelect;
}
